import { ThumbsUp } from 'lucide-react';
import React, { useState } from 'react';
import { API_URL, apiFetch } from '../../../config/api';
import { Suggestion } from '../../../types/flzhub';
import { Badge, Card } from './Shared';

const VOTED_KEY = 'flzhub_suggestion_votes';

function getVotedFromStorage(): Set<number> {
  try { return new Set(JSON.parse(localStorage.getItem(VOTED_KEY) ?? '[]') as number[]); }
  catch { return new Set(); }
}
function saveVotedToStorage(voted: Set<number>) {
  try { localStorage.setItem(VOTED_KEY, JSON.stringify([...voted])); } catch { }
}

interface Props {
  suggestions: Suggestion[];
}

export const FlzSuggestions: React.FC<Props> = ({ suggestions: initial }) => {
  const [suggestions, setSuggestions] = useState(initial);
  const [newText, setNewText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // FH4: voted state localStorage'da kalıcı
  const [voted, setVoted] = useState<Set<number>>(getVotedFromStorage);
  const [voteError, setVoteError] = useState<string | null>(null);

  const handleVote = async (id: number) => {
    if (voted.has(id)) return;
    setVoteError(null);
    try {
      const res = await apiFetch(`${API_URL}/api/flzhub/suggestions/${id}/vote`, { method: 'POST' });
      const d = await res.json();
      if (d.ok) {
        setSuggestions(prev => prev.map(s => s.id === id ? { ...s, votes: d.votes ?? (s.votes || 0) + 1 } : s));
        const newVoted = new Set([...voted, id]);
        setVoted(newVoted);
        saveVotedToStorage(newVoted);
      } else if (d.error === 'already_voted') {
        // Backend'den already_voted gelirse localStorage'a da ekle
        const newVoted = new Set([...voted, id]);
        setVoted(newVoted);
        saveVotedToStorage(newVoted);
      } else {
        setVoteError(d.message ?? 'Vote failed');
      }
    } catch { setVoteError('Connection error'); }
  };

  const handleSubmit = async () => {
    if (!newText.trim() || submitting) return;
    setSubmitting(true);
    try {
      const res = await apiFetch(`${API_URL}/api/flzhub/suggestions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ summary: newText.trim(), category: 'general' }),
      });
      const d = await res.json();
      if (d.suggestion) setSuggestions(prev => [d.suggestion, ...prev]);
      setNewText('');
    } catch { }
    setSubmitting(false);
  };

  return (
    <div style={{ height: '100%', overflowY: 'auto', padding: 20 }}>
      {/* New suggestion input */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        <input
          value={newText}
          onChange={e => setNewText(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSubmit()}
          placeholder="Suggest something for the family..."
          style={{ flex: 1, background: 'var(--os-input-bg)', border: '1px solid var(--os-border)', color: 'var(--os-text)', padding: '8px 12px', borderRadius: 8, fontSize: 11, outline: 'none' }}
        />
        <button
          onClick={handleSubmit}
          disabled={submitting || !newText.trim()}
          style={{ padding: '8px 14px', background: '#6366f1', border: 'none', color: '#fff', borderRadius: 8, fontSize: 11, cursor: 'pointer', opacity: submitting ? 0.5 : 1 }}
        >
          Submit
        </button>
      </div>

      {voteError && (
        <div style={{ padding: '6px 10px', background: '#ef444422', border: '1px solid #ef444444', borderRadius: 6, color: '#ef4444', fontSize: 10, marginBottom: 12 }}>
          {voteError}
        </div>
      )}

      {suggestions.length === 0 && (
        <div style={{ textAlign: 'center', color: 'var(--os-muted)', padding: 40, fontSize: 12 }}>No suggestions yet. Be the first!</div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
        {suggestions.map(sg => (
          <Card key={sg.id}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <span style={{ fontSize: 10, fontWeight: 800, color: '#6366f1', textTransform: 'uppercase' }}>{sg.category}</span>
              <Badge color={sg.status === 'approved' ? '#10b981' : '#f59e0b'}>{sg.status}</Badge>
            </div>
            <div style={{ fontSize: 12, lineHeight: 1.5, fontWeight: 500 }}>{sg.summary}</div>
            <div style={{ marginTop: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid var(--os-border)', paddingTop: 10 }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <span style={{ fontSize: 10, color: '#10b981', fontWeight: 700 }}>EQ {sg.eq_score?.toFixed(1)}</span>
                <button
                  onClick={() => handleVote(sg.id)}
                  disabled={voted.has(sg.id)}
                  title={voted.has(sg.id) ? 'Already voted' : 'Vote for this suggestion'}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 4,
                    background: voted.has(sg.id) ? 'rgba(99,102,241,0.15)' : 'rgba(255,255,255,0.05)',
                    border: voted.has(sg.id) ? '1px solid #6366f144' : 'none',
                    color: voted.has(sg.id) ? '#6366f1' : 'var(--os-muted)',
                    padding: '3px 8px', borderRadius: 6,
                    cursor: voted.has(sg.id) ? 'default' : 'pointer', fontSize: 10,
                  }}
                >
                  <ThumbsUp size={10} /> {sg.votes || 0}
                  {voted.has(sg.id) && <span style={{ fontSize: 8, marginLeft: 2 }}>✓</span>}
                </button>
              </div>
              <span style={{ fontSize: 9, color: 'var(--os-dim)' }}>{new Date(sg.created_at).toLocaleDateString()}</span>
            </div>
            {sg.baba_review && (
              <div style={{ marginTop: 10, padding: 8, background: 'rgba(99,102,241,0.05)', border: '1px solid rgba(99,102,241,0.1)', borderRadius: 8, fontSize: 10, fontStyle: 'italic', color: 'var(--os-muted)' }}>
                <span style={{ fontWeight: 800, color: '#6366f1', fontStyle: 'normal' }}>Baba Filiz: </span>{sg.baba_review}
              </div>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
};